import { MeshTransmissionMaterial, useGLTF } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import type { ReactElement } from 'react';
import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import type { GLTF } from 'three-stdlib';
import { bezier, reMap } from '../../../util/math.utils';
import useWindowSize from '../../../hooks/useWindowSize';
import { isTouch } from '../../../hooks/useIsDesktopDevice';

type GLTFResult = GLTF & {
  nodes: {
    BG: THREE.Mesh;
    Bonsai_1: THREE.Mesh;
  };
  materials: {
    ['RS Material']: THREE.MeshStandardMaterial;
    ['RS Material.2']: THREE.MeshStandardMaterial;
  };
};

type PropsType = {
  progressRef: React.RefObject<number>;
  backgroundColor?: string;
  shapeColor?: string;
  homepage?: boolean;
  isCurrent?: boolean;
};

export function Bonsai(props: PropsType): ReactElement {
  const group = useRef<THREE.Group>(null!);
  const { nodes, animations } = useGLTF('/assets/3d/Shapes/Bonsai.glb') as unknown as GLTFResult;
  const { width } = useWindowSize();
  const mixer = useRef<THREE.AnimationMixer>(undefined);
  const meshGroupRef = useRef<THREE.Group>(null);

  useEffect(() => {
    if (!group.current) return;
    mixer.current = new THREE.AnimationMixer(group.current);
    const action = mixer.current.clipAction(animations[0]);
    action.play();
  }, [animations]);

  useFrame(() => {
    if (!mixer?.current || !props.progressRef || !props.isCurrent || !meshGroupRef.current) return;
    if (props.progressRef.current < 100) {
      const p = props.progressRef.current;
      const b = bezier(p / 100, 0, 0.5, 0.5, 1);
      const t_bezier = b * animations[0].duration;
      const t = (p * animations[0].duration) / 100;
      mixer.current.setTime(props.homepage ? t_bezier : t);

      meshGroupRef.current.position.z = reMap(props.progressRef.current, 0, 100, 800, 200);
    }
  });

  const material = props.homepage ? (
    <MeshTransmissionMaterial
      backside
      samples={1}
      iridescence={1}
      iridescenceIOR={0.6}
      backsideResolution={32}
      iridescenceThicknessRange={[0, 200]}
      envMapIntensity={0.7}
      background={new THREE.Color('#4C4744')}
      distortion={0}
      distortionScale={0}
      temporalDistortion={0}
      clearcoat={1}
    />
  ) : (
    <MeshTransmissionMaterial
      backside
      transparent
      samples={1}
      iridescence={isTouch ? 0 : 0.5}
      iridescenceIOR={0.65}
      // resolution={32}
      backsideResolution={isTouch ? 6 : 32}
      iridescenceThicknessRange={[0, 100]}
      clearcoat={1}
      envMapIntensity={isTouch ? 0.1 : 0.8}
      opacity={0.85}
      // Adding any missing properties from previous components
      transmission={1}
      backsideThickness={0}
      chromaticAberration={0.03}
      transmissionSampler={false}
      roughness={0}
      clearcoatRoughness={0}
      reflectivity={0.5}
      sheenColor={props.shapeColor || '#FFFFFF'}
      sheen={0.5}
      metalness={0}
      ior={isTouch ? 0.8 : 1.5}
      specularColor="white"
      specularIntensity={isTouch ? 0.1 : 0.5}
      attenuationColor="white"
      thickness={0}
      anisotropicBlur={isTouch ? 0 : 0.1}
      distortion={0}
      distortionScale={0}
      temporalDistortion={0}
      background={new THREE.Color(props.shapeColor || '#4C4744')}
      emissive={props.shapeColor}
      emissiveIntensity={0.25}
    />
  );
  return (
    <group ref={group} {...props} dispose={null} visible={props.isCurrent}>
      <group ref={meshGroupRef} position={[0, 7, 0]}>
        <group name="Bonsai" position={[0, 0, 300]} rotation={[0, 0, 0]}>
          {props.homepage && (
            <mesh visible={false} geometry={nodes.Bonsai_1.geometry}>
              {material}
            </mesh>
          )}
          <mesh
            name="Bonsai_1"
            geometry={nodes.Bonsai_1.geometry}
            scale={Math.min(0.475, reMap(width, 320, 650, 0.275, 0.475))}
          >
            {material}
          </mesh>
        </group>
      </group>
    </group>
  );
}

useGLTF.preload('/assets/3d/Shapes/Bonsai.glb');

export default Bonsai;
